import React from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRight, Lock } from 'lucide-react';
import { ServiceItem } from '../types';

interface ServiceCardProps {
  item: ServiceItem;
  index: number;
}

const ServiceCard: React.FC<ServiceCardProps> = ({ item, index }) => {
  const isComingSoon = item.status === 'coming-soon';

  const borderColor = item.color === 'cyan' 
    ? 'group-hover:border-cyan-500/50 group-hover:shadow-[0_0_30px_-5px_rgba(6,182,212,0.3)]' 
    : item.color === 'purple'
    ? 'group-hover:border-purple-500/50 group-hover:shadow-[0_0_30px_-5px_rgba(139,92,246,0.3)]'
    : 'border-white/5';

  const iconColor = item.color === 'cyan' ? 'text-cyan-400' : item.color === 'purple' ? 'text-purple-400' : 'text-gray-500';

  return (
    <motion.a
      href={isComingSoon ? '#' : item.href}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={!isComingSoon ? { scale: 1.02, y: -5 } : {}}
      transition={{ type: "spring", stiffness: 400, damping: 25 }}
      className={`
        group relative flex flex-col p-6 h-64 w-full
        rounded-2xl border border-white/10 
        bg-white/[0.03] backdrop-blur-md
        transition-colors duration-300 ease-out
        ${isComingSoon ? 'cursor-not-allowed opacity-60 grayscale' : 'cursor-pointer'}
        ${!isComingSoon && borderColor}
        overflow-hidden
      `}
    >
      {/* Background Gradient Blob on Hover */}
      {!isComingSoon && (
        <div className={`
          absolute -top-20 -right-20 w-40 h-40 rounded-full blur-[50px] transition-opacity duration-500 opacity-0 group-hover:opacity-20
          ${item.color === 'cyan' ? 'bg-cyan-500' : 'bg-purple-500'}
        `} />
      )}

      <div className="flex justify-between items-start mb-6 z-10">
        <div className={`p-3 rounded-xl bg-white/5 border border-white/5 ${iconColor}`}>
          <item.icon size={28} strokeWidth={1.5} />
        </div>
        {isComingSoon ? (
           <span className="px-3 py-1 text-xs font-medium tracking-wider text-gray-400 uppercase border border-white/10 rounded-full bg-black/40">
             Soon
           </span>
        ) : (
          <ArrowUpRight className="text-gray-500 group-hover:text-white transition-colors duration-300" size={20} />
        )}
      </div>

      <div className="mt-auto z-10">
        <h3 className="text-xl font-display font-bold text-white mb-2 group-hover:tracking-wide transition-all duration-300">
          {item.title}
        </h3>
        <p className="text-sm text-gray-400 font-light leading-relaxed">
          {item.description}
        </p>
      </div>

      {isComingSoon && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/20 backdrop-blur-[1px] rounded-2xl z-20">
            <Lock className="text-white/20" size={48} />
        </div>
      )}
    </motion.a>
  );
};

export default ServiceCard;